import Table from '@shared/components/Table';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import DeleteModal from '@shared/components/DeleteModal';
import CompanyCentersForm from './CompanyCentersForm.jsx';
import { companiesService } from '@shared/services/companiesService';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig.js';
import { useParams, useSearchParams } from 'react-router-dom';

export default function CompanyCenters() {
    const { t } = useTranslation();
    const { companyId } = useParams(); // Get company ID from URL params
    const [searchParams] = useSearchParams(); // Get search parameters
    const [selectedCompanyCenter, setSelectedCompanyCenter] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [companyCentersData, setCompanyCentersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState({
        visible: false,
        type: null
    });

    // Get company details from URL parameters
    const companyName = searchParams.get('name');
    const companyBulstat = searchParams.get('bulstat');

    const columns = [
        { key: 'officeNo', label: t('organizationalStructure.companyBranch.companyCenters.costCenterCode') },
        { key: 'name', label: t('organizationalStructure.companyBranch.companyCenters.name') },
        { key: 'address', label: t('organizationalStructure.companyBranch.companyCenters.city') },
    ];

    // Fetch company centers when component mounts or companyId changes
    useEffect(() => {
        fetchCompanyCenters();
    }, []);

    const fetchCompanyCenters = async () => {
        try {
            setLoading(true);
            const centers = await companiesService.getCompanyCenters();
            setCompanyCentersData(centers);
        } catch (error) {
            console.error('Error fetching company centers:', error);
            notifyError(t('organizationalStructure.companyCenters.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const formatDateForApi = (date) => {
        if (!date) return null;
        if (date instanceof Date) return date.toISOString().split('T')[0];
        return date;
    };

    const buildSubmitData = (formData, isEdit = false) => {
        const submitData = {
            cityId: formData.cityId,
            branchId: parseInt(companyId),
            officeNo: formData.costCenterCode,
            name: formData.name,
            address: formData.address,
            phone: formData.phone,
            postCode: formData.postalCode,
            activity: formData.activity,
        };

        if (isEdit) {
            submitData.dateClosing = formatDateForApi(formData.dateClosing);
            submitData.reasonClosing = formData.reasonClosing;
        }

        return submitData;
    };

    const handleShowDeleteModal = () => {
        setModalState({ visible: true, type: 'delete' });
    };

    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => handleOpenModal('add')}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
            <button
                className={`icon-button ${!selectedCompanyCenter ? 'disabled-link' : ''}`}
                onClick={() => selectedCompanyCenter && handleOpenModal('edit')}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
                className={`icon-button ${!selectedCompanyCenter ? 'disabled-link' : ''}`}
                onClick={handleShowDeleteModal}
                disabled={!selectedCompanyCenter}
                title={t('common.delete')}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </>
    );

    const handleOpenModal = (type) => {
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedCompanyCenter(null);
        setSelectedData(null);
    };

    const handleConfirmDelete = async () => {
        try {
            if (!selectedCompanyCenter?.officeId) {
                notifyError(t('common.errorMessage'));
                return;
            }

            await companiesService.deleteCompanyCenter(selectedCompanyCenter.officeId);

            // Refresh the data and close modal
            await fetchCompanyCenters();
            handleCloseModal();

            // Show success message
            notifySuccess(t('common.deletedSuccessfully'));
        } catch (error) {
            console.error('Error deleting company center:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    const handleSelectRecord = (record) => {
        setSelectedCompanyCenter(record);
    };

    const handleConfirmAdd = async (formData) => {
        try {
            const submitData = buildSubmitData(formData, false);

            await companiesService.addCompanyCenter(submitData);

            // Refresh the data and close modal
            await fetchCompanyCenters();
            handleCloseModal();

            // Show success message
            notifySuccess(t('common.createdSuccessfully'));
        } catch (error) {
            console.error('Error adding company center:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    const handleConfirmEdit = async (formData) => {
        try {
            if (!selectedCompanyCenter?.officeId) {
                notifyError(t('common.errorMessage'));
                return;
            }

            const submitData = buildSubmitData(formData, true);

            await companiesService.updateCompanyCenter(selectedCompanyCenter.officeId, submitData);

            // Update the selectedCompanyCenter with the new data including location info
            const updatedCenter = {
                ...selectedCompanyCenter,
                ...submitData,
                // Preserve location data for editing
                region: formData.region,
                subregion: formData.subregion,
                cityId: formData.cityId
            };
            setSelectedCompanyCenter(updatedCenter);

            // Refresh the data and close modal
            await fetchCompanyCenters();
            handleCloseModal();

            // Show success message
            notifySuccess(t('common.updatedSuccessfully'));
        } catch (error) {
            console.error('Error updating company center:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    // Transform data to include id field for Table component compatibility
    const transformedData = companyCentersData.map(item => {
        return {
            ...item,
            id: item.officeId, // Add id field that Table component expects
            branchId: null // Set to null so Table component uses item.id for comparison
        };
    });


    const handleCheckboxChange = (index) => {
        const center = transformedData[index];

        // Convert both to strings to handle potential type mismatches
        const currentSelected = String(selectedData);
        const centerId = String(center.id);
        const newSelectedData = currentSelected === centerId ? null : center.id;

        setSelectedData(newSelectedData);
        setSelectedCompanyCenter(newSelectedData === null ? null : center);
    };

    return (
        <>
            <Table
                title={
                    <div>
                        <div>{t('organizationalStructure.companyBranch.companyCenters.title')}</div>
                        {companyName && companyBulstat && (
                            <div style={{ fontSize: '18px', fontWeight: '500', color: '#666', marginTop: '8px' }}>
                                {companyName} АД, {t('organizationalStructure.companyBranch.bulstat')}: {companyBulstat}
                            </div>
                        )}
                    </div>
                }
                columns={columns}
                data={transformedData}
                loading={loading}
                // error={error}
                selectedData={selectedData}
                setSelectedData={setSelectedData}
                showCompanySelect={true}
                showCustomSearch={true}
                customSearchComponent={null}
                showPageSizeSelector={false}
                showPagination={false}
                actionButtons={actionButtons}
                handleCheckboxChange={handleCheckboxChange}
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    selectedData={selectedData}
                    name={selectedCompanyCenter?.name}
                    entityType="record"
                />
            )}
            {modalState.visible && modalState.type === 'add' && (
                <CompanyCentersForm onClose={handleCloseModal} onConfirm={handleConfirmAdd} isEdit={false} />
            )}
            {modalState.visible && modalState.type === 'edit' && (
                <CompanyCentersForm
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmEdit}
                    editData={selectedCompanyCenter}
                    isEdit={true}
                />
            )}
        </>
    );
}